import { IStatItem } from './interfaces/IStatItem';
import { padNumber, round2 } from './utils';
import { IStatGroup } from './interfaces/IStatGroup';
import { IStats } from './interfaces/IStats';
import { ITableData } from './interfaces/ITableData';
import { ICurrency } from './interfaces/ICurrency';
import { IEntityStats } from './interfaces/IEntityStats';
import { StatsInterval } from './enums/StatsInterval';

export function statDateFormatted(stat: IStatItem) {
  let str = '';

  if (!stat) return str;

  if (stat.label) return stat.label;

  if (!stat.date) return str;

  if (stat.date.length === 6) {
    // yyyyMM
    str += `${stat.date.substr(4, 2)}/${stat.date.substr(0, 4)}`;
  } else {
    // yyyyMMdd
    str += `${stat.date.substr(6, 2)}/${stat.date.substr(
      4,
      2
    )}/${stat.date.substr(0, 4)}`;
  }

  if (typeof stat.hour !== 'undefined') str += ` ${padNumber(stat.hour, 2)}:00`;

  return str;
}

export function buildChartLabels(stats: IStats) {
  const labels: string[] = [];
  if (!stats || !stats.groups || !stats.groups.length) return labels;
  const sample = stats.groups[0].stats;
  switch (stats.interval) {
    case StatsInterval.Hourly:
      for (let i = 0; i < sample.length; i++) {
        const s = sample[i];
        const label = `${padNumber(s.hour, 2)}:00`;
        s.label = label;
        labels.push(label);
      }
      break;
    case StatsInterval.Daily:
      for (let i = 0; i < sample.length; i++) {
        const s = sample[i];
        const label = `${s.date.substr(6, 2)}/${s.date.substr(4, 2)}`;
        s.label = label;
        labels.push(label);
      }
      break;
    case StatsInterval.Weekly:
      for (let i = 0; i < sample.length; i++) {
        const s = sample[i];
        // s.date is the Monday (yyyyMMdd)
        const d = `${s.date.substr(6, 2)}/${s.date.substr(4, 2)}`;
        // compute week end (Sunday) label by adding 6 days visually; we just print as d/M to d/M
        // We do not have end date in stat, so we approximate by string math is not possible; however we only need labels.
        // Use a Date object to format properly
        const year = +s.date.substr(0, 4);
        const month = +s.date.substr(4, 2) - 1;
        const day = +s.date.substr(6, 2);
        const start = new Date(year, month, day);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
        const endStr = `${padNumber(end.getDate(), 2)}/${padNumber(
          end.getMonth() + 1,
          2
        )}`;
        const label = `${d} to ${endStr}`;
        s.label = label;
        labels.push(label);
      }
      break;
    case StatsInterval.Monthly:
      for (let i = 0; i < sample.length; i++) {
        const s = sample[i];
        const label = `${s.date.substr(4, 2)}/${s.date.substr(0, 4)}`;
        s.label = label;
        labels.push(label);
      }
      break;
  }
  return labels;
}

export function statValueFormatted(
  val: any,
  group: IStatGroup,
  currency: ICurrency
) {
  switch (group.type) {
    case 'number':
      return val;
    case 'currency':
      return currency.symbol + val;
  }
}

export function statsToTableData<T>(statsData: IStats | IEntityStats<T>) {
  const headers = [],
    rows = [],
    totals: any = {};

  // @ts-ignore
  const entities = statsData.entities,
    isEntities = !!entities;

  if (!isEntities) {
    headers.push({
      text: 'Fecha',
      value: 'date',
    });
  } else {
    headers.push({
      text: '',
      value: 'id',
      sortable: false,
    });
  }

  const groupsLength = statsData.groups.length,
    dataLength = statsData.groups[0].stats.length;

  for (const group of statsData.groups) {
    headers.push({
      text: [group.title],
      value: group.id,
    });
    totals[group.id] = 0;
  }

  for (let i = 0; i < dataLength; i++) {
    const row: any = {
      stat: statsData.groups[0].stats[i],
    };
    for (let j = 0; j < groupsLength; j++) {
      const group = statsData.groups[j],
        stat = group.stats[i];
      if (j === 0) {
        if (!isEntities)
          row.date =
            stat.date + (typeof stat.hour !== 'undefined' ? stat.hour : '');
        else row.id = stat.id;
      }
      row[group.id] = stat.value;
      totals[group.id] += stat.value;
    }
    rows.push(row);
  }

  for (const group of statsData.groups) {
    if (group.id === 'average' && dataLength > 0) {
      totals[group.id] /= dataLength;
    }
    totals[group.id] = round2(totals[group.id]);
  }

  return <ITableData>{
    headers,
    rows,
    totals,
  };
}
